import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../utils/translations';
import './Navbar.css';

const clientLabel = (c) =>
  c?.display_name || c?.alias_name || c?.name || '';

const Navbar = () => {
  const { isAuthenticated, user, logout, switchClient, isSuperAdmin, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [switching, setSwitching] = useState(false);
  const reportsActive = location.pathname === '/reports' || location.pathname.startsWith('/reports/');
  const [reportsOpen, setReportsOpen] = useState(reportsActive);

  useEffect(() => {
    if (reportsActive) setReportsOpen(true);
  }, [reportsActive]);

  const clients = Array.isArray(user?.clients) ? user.clients : [];
  const canSwitchClient = clients.length > 1;
  const currentClient = clients.find((c) => c.id === user?.client_id) || clients[0];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchClient = async (e) => {
    const nextId = e.target.value;
    if (!nextId || nextId === user?.client_id || switching) return;
    setSwitching(true);
    const result = await switchClient(nextId);
    setSwitching(false);
    if (!result.success) {
      alert(result.message || 'Failed to switch client');
      return;
    }
    // Reload so all pages refetch data for the new store
    navigate('/dashboard');
    window.location.reload();
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <nav className="sidebar">
        <div className="sidebar-header">
          <Link to="/dashboard" className="sidebar-brand">
            {t('Inventory System')}
          </Link>
        </div>
        <div className="sidebar-menu">
          <Link
            to="/dashboard"
            className={`sidebar-link ${location.pathname === '/dashboard' ? 'active' : ''}`}
          >
            <span className="sidebar-icon">📊</span>
            <span>{t('Dashboard')}</span>
          </Link>
          {/* Products - Only for admin and super_admin */}
          {isAdmin && (
            <>
              <Link
                to="/products"
                className={`sidebar-link ${location.pathname === '/products' ? 'active' : ''}`}
              >
                <span className="sidebar-icon">📦</span>
                <span>{t('Products')}</span>
              </Link>
              <Link
                to="/categories"
                className={`sidebar-link ${location.pathname === '/categories' ? 'active' : ''}`}
              >
                <span className="sidebar-icon">📁</span>
                <span>{t('Categories')}</span>
              </Link>
              <Link
                to="/subcategories"
                className={`sidebar-link ${location.pathname === '/subcategories' ? 'active' : ''}`}
              >
                <span className="sidebar-icon">📂</span>
                <span>{t('Subcategories')}</span>
              </Link>
            </>
          )}
          {/* Customers - Only for admin and super_admin */}
          {isAdmin && (
            <Link
              to="/customers"
              className={`sidebar-link ${location.pathname === '/customers' ? 'active' : ''}`}
            >
              <span className="sidebar-icon">👥</span>
              <span>{t('Customers')}</span>
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/suppliers"
              className={`sidebar-link ${location.pathname === '/suppliers' ? 'active' : ''}`}
            >
              <span className="sidebar-icon">🏭</span>
              <span>Suppliers</span>
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/purchases"
              className={`sidebar-link ${location.pathname === '/purchases' ? 'active' : ''}`}
            >
              <span className="sidebar-icon">📥</span>
              <span>Purchases</span>
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/combos"
              className={`sidebar-link ${location.pathname === '/combos' ? 'active' : ''}`}
            >
              <span className="sidebar-icon">🎁</span>
              <span>Combo offers</span>
            </Link>
          )}
          {isAdmin && user?.field_orders_enabled && (
            <Link
              to="/orders"
              className={`sidebar-link ${location.pathname === '/orders' ? 'active' : ''}`}
            >
              <span className="sidebar-icon">📦</span>
              <span>Field orders</span>
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/inventory"
              className={`sidebar-link ${location.pathname === '/inventory' ? 'active' : ''}`}
            >
              <span className="sidebar-icon">📋</span>
              <span>Inventory</span>
            </Link>
          )}
          {/* Invoices - Available for all users */}
          <Link
            to="/invoices"
            className={`sidebar-link ${location.pathname === '/invoices' ? 'active' : ''}`}
          >
            <span className="sidebar-icon">🧾</span>
            <span>{t('Invoices')}</span>
          </Link>
          <Link
            to="/pos"
            className={`sidebar-link ${location.pathname === '/pos' ? 'active' : ''}`}
          >
            <span className="sidebar-icon">🛒</span>
            <span>POS</span>
          </Link>
          <Link
            to="/returns"
            className={`sidebar-link ${location.pathname === '/returns' ? 'active' : ''}`}
          >
            <span className="sidebar-icon">↩️</span>
            <span>Returns</span>
          </Link>
          <Link
            to="/cash"
            className={`sidebar-link ${location.pathname === '/cash' ? 'active' : ''}`}
          >
            <span className="sidebar-icon">💵</span>
            <span>Cash Book</span>
          </Link>
          <Link
            to="/online-book"
            className={`sidebar-link ${location.pathname === '/online-book' ? 'active' : ''}`}
          >
            <span className="sidebar-icon">💳</span>
            <span>Online Book</span>
          </Link>
          <Link
            to="/expenses"
            className={`sidebar-link ${location.pathname === '/expenses' ? 'active' : ''}`}
          >
            <span className="sidebar-icon">💡</span>
            <span>Expenses</span>
          </Link>
          {isAdmin && (
            <Link
              to="/staff"
              className={`sidebar-link ${location.pathname === '/staff' ? 'active' : ''}`}
            >
              <span className="sidebar-icon">👷</span>
              <span>Staff</span>
            </Link>
          )}
          <div className={`sidebar-group ${reportsActive ? 'open' : ''}`}>
            <Link
              to="/reports"
              className={`sidebar-link ${location.pathname === '/reports' ? 'active' : reportsActive ? 'parent-active' : ''}`}
              onClick={() => setReportsOpen(true)}
            >
              <span className="sidebar-icon">📈</span>
              <span>Reports</span>
              <span
                className={`sidebar-caret ${reportsOpen ? 'open' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setReportsOpen((open) => !open);
                }}
                aria-hidden="true"
              >
                ▾
              </span>
            </Link>
            {reportsOpen && (
              <div className="sidebar-sublinks">
                <Link
                  to="/reports/customers"
                  className={`sidebar-link sub ${location.pathname === '/reports/customers' ? 'active' : ''}`}
                >
                  <span className="sidebar-icon">📒</span>
                  <span>Customer Report</span>
                </Link>
                <Link
                  to="/reports/gst"
                  className={`sidebar-link sub ${location.pathname === '/reports/gst' ? 'active' : ''}`}
                >
                  <span className="sidebar-icon">🧾</span>
                  <span>GST Report</span>
                </Link>
                {isAdmin && (
                  <Link
                    to="/reports/suppliers"
                    className={`sidebar-link sub ${location.pathname === '/reports/suppliers' ? 'active' : ''}`}
                  >
                    <span className="sidebar-icon">📗</span>
                    <span>Supplier Report</span>
                  </Link>
                )}
              </div>
            )}
          </div>
          {/* Users - Available for all users (admin and regular users) */}
          <Link
            to="/admin/users"
            className={`sidebar-link ${location.pathname === '/admin/users' ? 'active' : ''}`}
          >
            <span className="sidebar-icon">👤</span>
            <span>{t('Users')}</span>
          </Link>
          {/* Clients - Only for super_admin */}
          {isSuperAdmin && (
            <Link
              to="/admin/clients"
              className={`sidebar-link ${location.pathname === '/admin/clients' ? 'active' : ''}`}
            >
              <span className="sidebar-icon">🏢</span>
              <span>{t('Clients')}</span>
            </Link>
          )}
        </div>
      </nav>

      <header className="app-topbar">
        <div className="app-topbar-user">
          {canSwitchClient ? (
            <label className="app-topbar-switch">
              <span className="app-topbar-switch-label">Store</span>
              <select
                value={user?.client_id || ''}
                onChange={handleSwitchClient}
                disabled={switching}
                title="Switch store without logging out"
              >
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {clientLabel(c)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <div className="app-topbar-user-info">
            <span className="app-topbar-user-name">{user?.full_name}</span>
            <span className="app-topbar-user-role">
              {user?.role}
              {currentClient ? ` · ${clientLabel(currentClient)}` : ''}
            </span>
          </div>
          <button type="button" onClick={handleLogout} className="btn btn-secondary btn-sm app-topbar-logout">
            {t('Logout')}
          </button>
        </div>
      </header>
    </>
  );
};

export default Navbar;
